<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <template v-slot:header>
                <h5 class="mb-0">Detail Kas Kecil</h5>
            </template>

            <div class="detail-data-wrapper mb-3">
                <b-row>
                    <b-col cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Cabang</div>
                        <div class="font-weight-bold">{{ detail.cabang.name }}</div>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Kas Kecil Saat Ini</div>
                        <div class="font-weight-bold">{{ formatCurrency(currentCash) }}</div>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Nominal Pengajuan</div>
                        <div class="font-weight-bold">{{ formatCurrency(detail.requestedCash) }}</div>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Status</div>
                        <span :class="getCashStatusClass(detail.status)">
                            {{ getCashStatusLabel(detail.status) }}
                        </span>
                    </b-col>
                    <b-col v-if="!$helper.isNullOrEmpty(detail.numberCa)" cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Nomor CA</div>
                        <div>{{ detail.numberCa }}</div>
                    </b-col>
                    <b-col v-if="showNumberEc" cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Nomor EC</div>
                        <div>{{ detail.numberEc }}</div>
                    </b-col>
                    <b-col v-if="showNotesEc" cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Catatan EC</div>
                        <div>{{ detail.notesEc || "-" }}</div>
                    </b-col>
                    <b-col v-if="showTotalPendingKc" cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Perlu Dicek KC</div>
                        <div class="font-weight-bold">{{ detail.totalPendingKc || 0 }} Barang/Jasa</div>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-2">
                        <div class="small text-muted">Riwayat</div>
                        <b-button
                            size="sm"
                            variant="pgiBtn"
                            name="kasKecilRiwayatBtn"
                            @click="openHistory"
                        >
                            Riwayat
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <h6 class="mb-2 text-primary font-weight-bold">Log Pengeluaran</h6>
            <div class="kas-kecil-log-toolbar">
                <b-form-group label="No Invoice" class="mb-0 kas-kecil-search-wrapper">
                    <div class="kas-kecil-search-row">
                        <b-form-input
                            v-model.trim="filterNoInvoice"
                            name="kasKecilNoInvoiceSearchInput"
                            placeholder="Cari no invoice"
                            class="kas-kecil-search-input"
                            @keyup.enter="onSearchLogs"
                        />
                        <b-button variant="pgiBtn" name="kasKecilSearchLogBtn" @click="onSearchLogs">
                            <i class="fa fa-search"></i>
                        </b-button>
                        <b-button variant="pgiBtn" name="kasKecilResetSearchLogBtn" @click="onResetSearchLogs">
                            Reset
                        </b-button>
                    </div>
                </b-form-group>
                <div class="kas-kecil-action-row">
                    <b-button
                        v-if="canExportLaporanEc"
                        variant="success"
                        name="kasKecilExportLaporanEcBtn"
                        @click="onExportLaporanEc"
                    >
                        <i class="fa fa-file-excel-o mr-1"></i>
                        Export Laporan EC
                    </b-button>
                    <b-button
                        v-if="canRequestPenutupan"
                        class="kas-kecil-request-ec-btn"
                        name="kasKecilRequestPenutupanBtn"
                        @click="onRequestPenutupan"
                    >
                        Ajukan EC
                    </b-button>
                    <b-button
                        v-if="canApproveEc"
                        variant="danger"
                        name="kasKecilCancelEcBtn"
                        @click="onCancelEc"
                    >
                        Batalkan EC
                    </b-button>
                    <b-button
                        v-if="canApproveEc"
                        class="kas-kecil-approve-ec-btn"
                        name="kasKecilApproveEcBtn"
                        @click="onApproveEc"
                    >
                        Setujui EC
                    </b-button>
                    <b-button
                        v-if="canInputPengeluaran"
                        variant="pgiBtn"
                        name="kasKecilInputPengeluaranBtn"
                        @click="onInputPengeluaran"
                    >
                        Input Pengeluaran
                    </b-button>
                </div>
            </div>

            <b-table
                id="kas-kecil-log-table"
                bordered
                hover
                show-empty
                responsive
                :busy="isLoadingLogs"
                :items="items"
                :fields="fields"
                class="mb-3"
            >
                <template v-slot:table-busy>
                    <div class="text-center my-3">
                        <b-spinner small class="mr-2" />
                        Memuat data pengeluaran...
                    </div>
                </template>
                <template v-slot:empty>
                    Tidak ada data pengeluaran.
                </template>
                <template v-slot:cell(itemType)="row">
                    <div class="smallText">{{ row.item.itemType }}</div>
                    <div class="smallText text-muted">
                        {{ row.item.categoryName }}
                    </div>
                    <div class="smallText text-muted">
                        {{ formatCurrency(row.item.price) }} x {{ row.item.qty }}
                    </div>
                </template>
                <template v-slot:cell(noInvoice)="row">
                    <div class="smallText">{{ row.item.noInvoice || "-" }}</div>
                    <div class="smallText text-muted">{{ row.item.createdBy || "-" }}</div>
                </template>
                <template v-slot:cell(total)="row">
                    <div class="smallText text-right font-weight-bold">
                        {{ formatCurrency(row.item.total || (row.item.price * row.item.qty)) }}
                    </div>
                </template>
                <template v-slot:cell(notes)="row">
                    <b-button
                        size="sm"
                        variant="pgiBtn"
                        name="kasKecilCatatanBtn"
                        @click="openNotes(row.item)"
                    >
                        Catatan
                    </b-button>
                </template>
                <template v-slot:cell(status)="row">
                    <div class="text-center">
                        <span :class="getLogStatusClass(row.item.status)" class="kas-kecil-status-badge">
                            {{ getLogStatusLabel(row.item.status) }}
                        </span>
                        <span v-if="isLogChecked(row.item)" class="status-box-md status-box-success kas-kecil-status-badge mt-1">
                            Yakin Benar
                        </span>
                    </div>
                </template>
                <template v-slot:cell(action)="row">
                    <div class="kas-kecil-action-wrapper">
                        <b-button
                            v-if="canConfirmYakinBenar(row.item)"
                            size="sm"
                            class="kas-kecil-action-btn kas-kecil-approve-ec-btn"
                            name="kasKecilYakinBenarBtn"
                            @click="onYakinBenar(row.item)"
                        >
                            Yakin Benar
                        </b-button>
                        <b-button
                            v-if="hasPhoto(row.item)"
                            size="sm"
                            variant="primary"
                            class="kas-kecil-action-btn"
                            name="kasKecilLihatFotoBtn"
                            @click="onShowPhoto(row.item)"
                        >
                            Lihat Foto
                        </b-button>
                        <b-button
                            v-if="canUploadReceipt(row.item)"
                            size="sm"
                            variant="success"
                            class="kas-kecil-action-btn"
                            name="kasKecilUploadBuktiDiterimaBtn"
                            @click="onUploadReceipt(row.item)"
                        >
                            Upload Bukti
                        </b-button>
                        <b-button
                            v-if="canRefund(row.item)"
                            size="sm"
                            variant="warning"
                            class="kas-kecil-action-btn"
                            name="kasKecilRefundBtn"
                            @click="onRefund(row.item)"
                        >
                            Refund
                        </b-button>
                        <b-button
                            v-if="canVoid(row.item)"
                            size="sm"
                            variant="danger"
                            class="kas-kecil-action-btn"
                            name="kasKecilVoidBtn"
                            @click="onVoid(row.item)"
                        >
                            Void
                        </b-button>
                    </div>
                </template>
                <template v-slot:cell(createdAt)="row">
                    <div class="smallText">{{ formatDate(row.item.createdAt) }}</div>
                    <div class="smallText text-muted">{{ formatTime(row.item.createdAt) }}</div>
                </template>
            </b-table>

            <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                    <b-form-select v-model="show" @change="onLimitChange" :options="optionsLimit">
                        <template v-slot:first>
                            <b-form-select-option :value="null" disabled />
                        </template>
                    </b-form-select>
                    <span class="ml-2">{{ totalViewText }}</span>
                </b-input-group>
                <b-pagination
                    class="mb-0"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    aria-controls="kas-kecil-log-table"
                    @input="onPageChange"
                />
            </div>
        </b-card>

        <ModalKasKecilRequestPenutupan
            :isSubmitting="isSubmittingRequestPenutupan"
            :currentCash.sync="confirmCurrentCash"
            :notes.sync="requestPenutupanNotes"
            @submit="submitRequestPenutupan"
        />
        <ModalKasKecilCancelEc :isSubmitting="isSubmittingCancelEc" @submit="submitCancelEc" />
        <ModalKasKecilApproveEc :isSubmitting="isSubmittingEcApproval" @submit="submitEcApproval" />
        <ModalKasKecilYakinBenar :isSubmitting="isSubmittingYakinBenar" @submit="submitYakinBenar" />
        <ModalKasKecilRiwayat :items="historyTimeline" />
        <ModalKasKecilReason
            :title="reasonModalTitle"
            :desc="reasonModalDesc"
            :isSubmitting="isSubmittingReason"
            :reason.sync="reason"
            @submit="submitReasonAction"
        />
        <ModalKasKecilUploadBuktiConfirm :isSubmitting="isSubmittingReceipt" @submit="submitReceipt" />
        <ModalKasKecilExportEcConfirm :isSubmitting="isSubmittingExportEc" @submit="submitExportLaporanEc" />

        <ModalKasKecilUploadReceipt
            v-model="isShowUploadReceipt"
            :item="selectedReceiptItem"
            :photos="receiptPhotos"
            :isSubmitting="isSubmittingReceipt"
            @add-photo="addReceiptPhoto"
            @remove-photo="removeReceiptPhoto"
            @upload-photo="({ photo, file }) => onUploadFile(photo, file)"
            @preview-photo="photo => openFilePreview(photo, 'Bukti Diterima')"
            @submit="openSubmitReceiptConfirm"
        />

        <ModalKasKecilFilePreview
            id="modal-preview-file-kas-kecil-receipt"
            :title="activeFilePreview.title"
            :url="activeFilePreview.url"
            :type="activeFilePreview.type"
        />
        <ModalKasKecilLogPhotoPreview
            modalId="modal-preview-log-kas-kecil-detail"
            :title="activeLogPhotoPreview.title"
            :url="activeLogPhotoPreview.url"
            :files="activeLogPhotoPreview.files"
            @switch-photo="setActiveLogPhoto"
        />
        <ModalKasKecilFilePreview
            id="modal-preview-export-laporan-ec"
            title="Export Laporan EC"
            :url="exportEcUrl"
            type="pdf"
        />
        <ModalKasKecilCatatan
            :item="selectedNotesItem"
            :showReason="notesShowReason"
            :reasonLabel="notesReasonLabel"
        />
    </div>
</template>

<script>
import CONSTANTS from '@/constants'
import ModalKasKecilRequestPenutupan from '@/components/modals/ModalKasKecilRequestPenutupan'
import ModalKasKecilCancelEc from '@/components/modals/ModalKasKecilCancelEc'
import ModalKasKecilApproveEc from '@/components/modals/ModalKasKecilApproveEc'
import ModalKasKecilYakinBenar from '@/components/modals/ModalKasKecilYakinBenar'
import ModalKasKecilRiwayat from '@/components/modals/ModalKasKecilRiwayat'
import ModalKasKecilReason from '@/components/modals/ModalKasKecilReason'
import ModalKasKecilUploadBuktiConfirm from '@/components/modals/ModalKasKecilUploadBuktiConfirm'
import ModalKasKecilExportEcConfirm from '@/components/modals/ModalKasKecilExportEcConfirm'
import ModalKasKecilUploadReceipt from '@/components/modals/ModalKasKecilUploadReceipt'
import ModalKasKecilFilePreview from '@/components/modals/ModalKasKecilFilePreview'
import ModalKasKecilLogPhotoPreview from '@/components/modals/ModalKasKecilLogPhotoPreview'
import ModalKasKecilCatatan from '@/components/modals/ModalKasKecilCatatan'

const STATUS_KAS_KECIL = CONSTANTS.STATUS_KAS_KECIL
const STATUS_LOG = CONSTANTS.STATUS_LOG
const MAX_UPLOAD_COUNT = 5

export default {
    name: "detail-kas-kecil",
    components: {
        ModalKasKecilRequestPenutupan,
        ModalKasKecilCancelEc,
        ModalKasKecilApproveEc,
        ModalKasKecilYakinBenar,
        ModalKasKecilRiwayat,
        ModalKasKecilReason,
        ModalKasKecilUploadBuktiConfirm,
        ModalKasKecilExportEcConfirm,
        ModalKasKecilUploadReceipt,
        ModalKasKecilFilePreview,
        ModalKasKecilLogPhotoPreview,
        ModalKasKecilCatatan,
    },
    data() {
        return {
            initStatus: 0,
            detail: {
                id: Number(this.$route.params.id || 0),
                cabang: { id: null, name: "-" },
                kasKecil: null,
                requestedCash: 0,
                numberCa: null,
                numberEc: null,
                status: null,
                createdAt: null,
                createdBy: "-",
                kcApprovedAt: null,
                kcApprovedBy: null,
                requestClosedAt: null,
                requestClosedBy: null,
                approvalClosedKcUserAt: null,
                approvalClosedKcUserBy: null,
                claimedAt: null,
                claimedBy: null,
                refundedAt: null,
                refundedBy: null,
                notesEc: null,
                totalPendingKc: 0,
            },
            isShowUploadReceipt: false,
            isSubmittingRequestPenutupan: false,
            isSubmittingEcApproval: false,
            isSubmittingCancelEc: false,
            isSubmittingYakinBenar: false,
            isSubmittingReason: false,
            isSubmittingReceipt: false,
            isSubmittingExportEc: false,
            isLoadingLogs: false,
            filterStatus: null,
            filterNoInvoice: "",
            confirmCurrentCash: "",
            requestPenutupanNotes: "",
            exportEcUrl: "",
            selectedNotesItem: null,
            selectedYakinBenarItem: null,
            selectedReasonItem: null,
            reasonAction: "",
            reason: "",
            selectedReceiptItem: null,
            nextReceiptPhotoIndex: 2,
            receiptPhotos: [this.createUploadFoto(1)],
            activeFilePreview: {
                title: "Preview Upload",
                type: "image",
                url: "",
            },
            activeLogPhotoPreview: {
                title: "Preview Foto",
                url: "",
                files: [],
            },
            currentPage: 1,
            show: 10,
            perPage: 10,
            totalRows: 0,
            totalViewText: "0 dari 0 data",
            optionsLimit: CONSTANTS.OPTIONS_LIMIT_PAGINATION,
            fields: [
                { key: "createdAt", label: "Tanggal", thClass: "kas-kecil-date-col", tdClass: "kas-kecil-date-col" },
                { key: "noInvoice", label: "Invoice / Pembuat", thClass: "kas-kecil-invoice-col", tdClass: "kas-kecil-invoice-col" },
                { key: "itemType", label: "Barang/Jasa", thClass: "kas-kecil-item-col", tdClass: "kas-kecil-item-col" },
                { key: "total", label: "Total", thClass: "kas-kecil-total-col", tdClass: "kas-kecil-total-col" },
                { key: "notes", label: "Catatan", thClass: "kas-kecil-notes-col", tdClass: "kas-kecil-notes-col" },
                { key: "status", label: "Status", thClass: "kas-kecil-status-col text-center", tdClass: "kas-kecil-status-col text-center" },
                { key: "action", label: "Aksi", thClass: "kas-kecil-action-col", tdClass: "kas-kecil-action-col" },
            ],
            items: [],
        }
    },
    computed: {
        activeExpenseTotal() {
            return this.items
                .filter((item) => item.status !== STATUS_LOG.VOID && item.status !== STATUS_LOG.REFUND)
                .reduce((total, item) => total + (Number(item.price || 0) * Number(item.qty || 0)), 0)
        },
        currentCash() {
            if (this.hasCashValue(this.detail.kasKecil)) return Number(this.detail.kasKecil || 0)
            return Number(this.detail.requestedCash || 0) - this.activeExpenseTotal
        },
        confirmCurrentCashValue() {
            return Number(this.$helper.removeThousandSeparator(this.confirmCurrentCash || 0))
        },
        isStatusBerjalan() {
            return Number(this.detail.status) === STATUS_KAS_KECIL.BERJALAN
        },
        isStatusRequestClosed() {
            return Number(this.detail.status) === STATUS_KAS_KECIL.REQUEST_CLOSED
        },
        canUpdateLogStatus() {
            return [
                STATUS_KAS_KECIL.BERJALAN,
                STATUS_KAS_KECIL.REQUEST_CLOSED,
                STATUS_KAS_KECIL.PENDING_PENGEMBALIAN,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(Number(this.detail.status))
        },
        showNotesEc() {
            return [
                STATUS_KAS_KECIL.REQUEST_CLOSED,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(Number(this.detail.status))
        },
        showTotalPendingKc() {
            return [
                STATUS_KAS_KECIL.REQUEST_CLOSED,
                STATUS_KAS_KECIL.PENDING_PENGEMBALIAN,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(Number(this.detail.status))
        },
        canRequestPenutupan() {
            return this.isStatusBerjalan && this.$helper.hasPermissionMenuChildren("MRC")
        },
        canApproveEc() {
            return this.isStatusRequestClosed && this.$helper.hasPermissionMenuChildren("MAV")
        },
        canInputPengeluaran() {
            return this.isStatusBerjalan
                && (
                    this.$helper.hasPermissionMenuChildren("MIE")
                    || this.$helper.hasPermissionMenuChildren("MBH")
                )
        },
        showNumberEc() {
            return [
                STATUS_KAS_KECIL.PENDING_PENGEMBALIAN,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(Number(this.detail.status))
        },
        canExportLaporanEc() {
            return this.showNumberEc
        },
        reasonModalTitle() {
            if (this.reasonAction === "refund") return "Konfirmasi Refund Pengeluaran"
            return "Konfirmasi Void Pengeluaran"
        },
        reasonModalDesc() {
            if (this.reasonAction === "refund") return "Apakah Anda yakin ingin refund pengeluaran ini?"
            return "Apakah Anda yakin ingin melakukan void pengeluaran ini?"
        },
        historyTimeline() {
            const d = this.detail
            const entries = [
                { show: Boolean(d.createdBy || d.createdAt), icon: "fa-clipboard", color: "blue", title: "Pengajuan CA", by: d.createdBy || "-", at: this.formatDateTime(d.createdAt) },
                { show: this.hasHistoryInfo(d.kcApprovedBy, d.kcApprovedAt), icon: "fa-check", color: "green", title: "Approval CA", by: this.getApprovalName(d.kcApprovedBy, d.kcApprovedAt), at: this.formatDateTime(d.kcApprovedAt) },
                { show: this.hasHistoryInfo(d.claimedBy, d.claimedAt), icon: "fa-money", color: "orange", title: "Klaim Kas", by: this.formatHistoryBy(d.claimedBy), at: this.formatDateTime(d.claimedAt) },
                { show: this.hasHistoryInfo(d.requestClosedBy, d.requestClosedAt), icon: "fa-file-text-o", color: "yellow", title: "Pengajuan EC", by: this.formatHistoryBy(d.requestClosedBy), at: this.formatDateTime(d.requestClosedAt) },
                { show: this.hasHistoryInfo(d.approvalClosedKcUserBy, d.approvalClosedKcUserAt), icon: "fa-check-circle-o", color: "green", title: "Approval EC", by: this.formatHistoryBy(d.approvalClosedKcUserBy), at: this.formatDateTime(d.approvalClosedKcUserAt) },
                { show: this.hasHistoryInfo(d.refundedBy, d.refundedAt), icon: "fa-undo", color: "purple", title: "Proses Pengembalian", by: this.formatHistoryBy(d.refundedBy), at: this.formatDateTime(d.refundedAt) },
            ]
            return entries.filter((entry) => entry.show)
        },
        notesShowReason() {
            return this.selectedNotesItem ? this.showVoidRefundReason(this.selectedNotesItem) : false
        },
        notesReasonLabel() {
            return this.selectedNotesItem ? this.voidRefundReasonLabel(this.selectedNotesItem) : ""
        },
    },
    methods: {
        hasCashValue(value) {
            return value !== null && value !== undefined && value !== ""
        },
        async init() {
            this.initStatus = 0
            try {
                await this.getExpenseLogs()
                this.initStatus = 1
            } catch (error) {
                this.$helper.parseError(this, error)
                this.initStatus = -1
            }
        },
        getExpenseLogs(page = 1) {
            this.currentPage = isNaN(page) ? 1 : page
            this.isLoadingLogs = true

            return this.$axios.get("mini-cash/get-log", {
                params: this.buildExpenseLogParams(),
            }).then((response) => {
                const data = Array.isArray(response.data && response.data.data)
                    ? response.data.data[0]
                    : null

                if (!data) {
                    this.items = []
                    this.totalRows = 0
                    this.updateTotalViewText()
                    return
                }

                this.mapMiniCashDetail(data)
                const logs = this.getExpenseLogItems(data)
                this.items = logs.map((item) => this.mapExpenseLogItem(item))
                this.totalRows = this.getExpenseLogTotalRows(response.data, data, logs)
                this.updateTotalViewText()
            }).catch((error) => {
                throw error
            }).finally(() => {
                this.isLoadingLogs = false
            })
        },
        buildExpenseLogParams() {
            const params = {
                miniCashCabangId: this.detail.id,
                page: this.currentPage - 1,
                size: this.perPage,
            }

            if (!this.$helper.isNullOrEmpty(this.filterStatus)) {
                params.filterStatus = this.filterStatus
            }

            if (!this.$helper.isNullOrEmpty(this.filterNoInvoice)) {
                params.filterNoInvoice = this.filterNoInvoice
            }

            return params
        },
        getExpenseLogItems(data) {
            const logs = data.logs
            if (Array.isArray(logs)) return logs
            if (logs && Array.isArray(logs.data)) return logs.data
            if (logs && Array.isArray(logs.content)) return logs.content
            return []
        },
        getExpenseLogTotalRows(result, data, logs) {
            const logsPayload = data.logs || {}
            const total = result.total
                || result.totalElements
                || data.total
                || data.totalLogs
                || data.totalElements
                || logsPayload.total
                || logsPayload.totalElements
                || logs.length
            return Number(total || 0)
        },
        updateTotalViewText() {
            if (this.totalRows === 0) {
                this.totalViewText = "0 dari 0 data"
                return
            }

            const pg = this.currentPage - 1
            const prev = pg * this.show + 1
            let limit = pg * this.show + this.show
            if (limit > this.totalRows) limit = this.totalRows
            this.totalViewText = `Menampilkan ${prev} - ${limit} dari ${this.totalRows} entri`
        },
        mapMiniCashDetail(item) {
            this.detail = {
                ...this.detail,
                id: item.miniCashId || this.detail.id,
                cabang: {
                    id: item.cabangId || this.detail.cabang.id,
                    name: item.namaCabang || this.detail.cabang.name,
                },
                kasKecil: item.kasKecil,
                requestedCash: item.requestedCash || 0,
                numberCa: item.numberCa,
                numberEc: item.numberEc,
                status: Number(item.status),
                createdAt: item.createdAt,
                createdBy: item.createdBy || "-",
                kcApprovedAt: item.kcApprovedAt,
                kcApprovedBy: item.kcApprovedBy,
                requestClosedAt: item.requestClosedAt,
                requestClosedBy: item.requestClosedBy,
                approvalClosedKcUserAt: item.approvalClosedKcUserAt,
                approvalClosedKcUserBy: item.approvalClosedKcUserBy,
                claimedAt: item.claimedAt,
                claimedBy: item.claimedBy,
                refundedAt: item.refundedAt,
                refundedBy: item.refundedBy,
                notesEc: item.notesEc,
                totalPendingKc: item.totalPendingKc || 0,
                closedAt: item.closedAt,
            }
        },
        mapExpenseLogItem(item) {
            return {
                id: item.id,
                noInvoice: item.noInvoice,
                createdAt: item.createdAt,
                createdBy: item.createdBy,
                categoryName: item.categoryName || item.category || item.unit || "",
                itemType: item.itemType || item.miniCashItemType || "-",
                price: item.pricePerQty || item.price || 0,
                qty: item.qty || 0,
                total: item.total || 0,
                status: Number(item.status),
                invoiceFile: this.normalizePhotoList(item.fotoInvoice || item.photoInvoice || item.invoiceFile),
                receiptFile: this.normalizePhotoList(item.fotoBuktiBarang || item.photoProof || item.receiptFile),
                notes: item.notes || "",
                voidReason: item.voidReason || "",
                kcStatus: Number(item.kcStatus || 0),
                raw: item,
            }
        },
        normalizePhotoList(value) {
            if (Array.isArray(value)) return value.filter((item) => !this.$helper.isNullOrEmpty(item))
            if (this.$helper.isNullOrEmpty(value)) return []
            return [value]
        },
        onInputPengeluaran() {
            if (this.currentCash <= 0) {
                this.$helper.toastErr(this, "Kas kecil saat ini adalah Rp. 0")
                return
            }
            this.$router.push({ path: `/kasir/kas-kecil/${this.detail.id}/input-pengeluaran` })
        },
        onSearchLogs() {
            this.getExpenseLogs(1)
        },
        onResetSearchLogs() {
            this.filterNoInvoice = ""
            this.getExpenseLogs(1)
        },
        onLimitChange(newPerPage) {
            this.show = newPerPage
            this.perPage = newPerPage
            this.getExpenseLogs(1)
        },
        onPageChange(newPage) {
            this.getExpenseLogs(newPage)
        },
        onExportLaporanEc() {
            this.$bvModal.show("modal-confirm-export-laporan-ec-kas-kecil")
        },
        submitExportLaporanEc() {
            if (!this.detail || !this.detail.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingExportEc = true
            this.$axios.get("mini-cash/export-log-ec", {
                params: {
                    miniCashCabangId: this.detail.id,
                },
            }).then((response) => {
                const result = response.data || {}
                if (this.$helper.isNullOrEmpty(result.name)) {
                    this.$helper.toastErr(this, "File laporan EC tidak ditemukan.")
                    return
                }

                this.exportEcUrl = this.buildBaseFileUrl(result.name)
                this.$bvModal.hide("modal-confirm-export-laporan-ec-kas-kecil")
                this.$bvModal.show("modal-preview-export-laporan-ec")
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingExportEc = false
            })
        },
        onRequestPenutupan() {
            this.confirmCurrentCash = ""
            this.requestPenutupanNotes = ""
            this.$bvModal.show("modal-confirm-request-penutupan-kas-kecil")
        },
        onApproveEc() {
            this.$bvModal.show("modal-confirm-approve-ec-kas-kecil")
        },
        onCancelEc() {
            this.$bvModal.show("modal-confirm-cancel-ec-kas-kecil")
        },
        openHistory() {
            this.$bvModal.show("modal-riwayat-kas-kecil")
        },
        onYakinBenar(item) {
            this.selectedYakinBenarItem = item
            this.$bvModal.show("modal-confirm-yakin-benar-kas-kecil")
        },
        submitRequestPenutupan() {
            if (!this.detail || !this.detail.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            if (!this.validateConfirmCurrentCash()) return

            this.isSubmittingRequestPenutupan = true
            const params = {
                miniCashCabangId: this.detail.id,
            }

            if (!this.$helper.isNullOrEmpty(this.requestPenutupanNotes)) {
                params.notesEc = this.requestPenutupanNotes
            }

            this.$axios.post("mini-cash/request-close", {}, { params }).then(() => {
                this.$bvModal.hide("modal-confirm-request-penutupan-kas-kecil")
                this.$helper.toastSucc(this, "Pengajuan EC berhasil dibuat.")
                this.getExpenseLogs()
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingRequestPenutupan = false
            })
        },
        submitEcApproval() {
            if (!this.detail || !this.detail.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingEcApproval = true
            this.$axios.post("mini-cash/approval-close", {}, {
                params: {
                    miniCashCabangId: this.detail.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-approve-ec-kas-kecil")
                this.$helper.toastSucc(this, "EC berhasil disetujui.")
                this.getExpenseLogs()
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingEcApproval = false
            })
        },
        submitCancelEc() {
            if (!this.detail || !this.detail.id) {
                this.$helper.toastErr(this, "ID kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingCancelEc = true
            this.$axios.post("mini-cash/cancel-request-close/", {}, {
                params: {
                    miniCashCabangId: this.detail.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-cancel-ec-kas-kecil")
                this.$helper.toastSucc(
                    this,
                    "Pengajuan EC dibatalkan, Kas Kecil kembali ke status Berjalan.",
                    "Pengajuan EC Dibatalkan"
                )
                this.getExpenseLogs()
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingCancelEc = false
            })
        },
        submitYakinBenar() {
            if (!this.selectedYakinBenarItem || !this.selectedYakinBenarItem.id) {
                this.$helper.toastErr(this, "ID log kas kecil tidak ditemukan.")
                return
            }

            this.isSubmittingYakinBenar = true
            this.$axios.post("mini-cash/check-log", {}, {
                params: {
                    miniCashCabangLogId: this.selectedYakinBenarItem.id,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-yakin-benar-kas-kecil")
                this.$helper.toastSucc(this, "Data kas kecil berhasil ditandai yakin benar.")
                this.selectedYakinBenarItem = null
                this.getExpenseLogs()
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingYakinBenar = false
            })
        },
        onRefund(item) {
            this.openReasonModal("refund", item)
        },
        onVoid(item) {
            this.openReasonModal("void", item)
        },
        openReasonModal(action, item) {
            this.reasonAction = action
            this.selectedReasonItem = item
            this.reason = ""
            this.$bvModal.show("modal-confirm-reason-log-kas-kecil")
        },
        submitReasonAction() {
            if (!this.selectedReasonItem || !this.selectedReasonItem.id) {
                this.$helper.toastErr(this, "ID log kas kecil tidak ditemukan.")
                return
            }

            if (this.$helper.isNullOrEmpty(this.reason)) {
                this.$helper.toastErr(this, "Alasan wajib diisi.")
                return
            }

            if (this.reason.length > 255) {
                this.$helper.toastErr(this, "Alasan maksimal 255 karakter.")
                return
            }

            const path = this.reasonAction === "refund" ? "mini-cash/void-refund" : "mini-cash/void-log"
            const successMessage = this.reasonAction === "refund"
                ? "Pengeluaran berhasil direfund."
                : "Pengeluaran berhasil divoid."

            this.isSubmittingReason = true
            this.$axios.post(path, {}, {
                params: {
                    miniCashLogId: this.selectedReasonItem.id,
                    reason: this.reason,
                },
            }).then(() => {
                this.$bvModal.hide("modal-confirm-reason-log-kas-kecil")
                this.$helper.toastSucc(this, successMessage)
                this.selectedReasonItem = null
                this.reasonAction = ""
                this.reason = ""
                this.getExpenseLogs()
            }).catch((error) => {
                this.$helper.parseError(this, error)
            }).finally(() => {
                this.isSubmittingReason = false
            })
        },
        canVoid(item) {
            return this.canUpdateLogStatus
                && this.$helper.hasPermissionMenuChildren("MVR")
                && (item.status === STATUS_LOG.OK || item.status === STATUS_LOG.SEDANG_PEMESANAN)
        },
        canRefund(item) {
            return this.canVoid(item)
        },
        canConfirmYakinBenar(item) {
            return [
                STATUS_KAS_KECIL.REQUEST_CLOSED,
                STATUS_KAS_KECIL.PENDING_PENGEMBALIAN,
                STATUS_KAS_KECIL.CLOSED,
            ].includes(Number(this.detail.status))
                && this.$helper.hasPermissionMenuChildren("MAV")
                && Number(item.kcStatus || 0) === 0
        },
        canUploadReceipt(item) {
            return this.$helper.hasPermissionMenuChildren("MIE")
                && item.status === STATUS_LOG.SEDANG_PEMESANAN
                && this.normalizePhotoList(item.receiptFile).length === 0
        },
        hasPhoto(item) {
            return this.normalizePhotoList(item.invoiceFile).length > 0 || this.normalizePhotoList(item.receiptFile).length > 0
        },
        onShowPhoto(item) {
            const files = this.getLogPhotoFiles(item)
            if (files.length === 0) {
                this.$helper.toastErr(this, "File tidak tersedia.")
                return
            }

            this.activeLogPhotoPreview = {
                title: "Lihat Foto",
                url: files[0].url,
                files,
            }
            this.$bvModal.show("modal-preview-log-kas-kecil-detail")
        },
        getLogPhotoFiles(item) {
            const invoiceFiles = this.normalizePhotoList(item.invoiceFile).map((file, index) => ({
                label: `Invoice ${index + 1}`,
                url: this.buildFileUrl(file),
            }))
            const proofFiles = this.normalizePhotoList(item.receiptFile).map((file, index) => ({
                label: `Bukti ${index + 1}`,
                url: this.buildFileUrl(file),
            }))

            return invoiceFiles.concat(proofFiles).filter((file) => !this.$helper.isNullOrEmpty(file.url))
        },
        buildFileUrl(path) {
            if (this.$helper.isNullOrEmpty(path)) return ""
            if (/^https?:\/\//.test(path)) return path

            const basePath = String(this.$baseFilePathAws || "").replace(/\/$/, "")
            const filePath = String(path).replace(/^\//, "")
            return `${basePath}/${filePath}`
        },
        buildBaseFileUrl(path) {
            if (this.$helper.isNullOrEmpty(path)) return ""
            if (/^https?:\/\//.test(path)) return path

            const basePath = String(this.$baseFilePath || "").replace(/\/$/, "")
            const filePath = String(path).replace(/^\//, "")
            return `${basePath}/${filePath}`
        },
        setActiveLogPhoto(file) {
            this.activeLogPhotoPreview.url = file.url
        },
        openNotes(item) {
            this.selectedNotesItem = item
            this.$bvModal.show("modal-catatan-log-kas-kecil")
        },
        showVoidRefundReason(item) {
            return item.status === STATUS_LOG.VOID || item.status === STATUS_LOG.REFUND
        },
        voidRefundReasonLabel(item) {
            if (item.status === STATUS_LOG.VOID) return "Catatan Void"
            return "Catatan Refund"
        },
        onUploadReceipt(item) {
            this.selectedReceiptItem = item
            this.receiptPhotos.forEach((photo) => this.revokeUploadPreview(photo))
            this.receiptPhotos = [this.createUploadFoto(1)]
            this.nextReceiptPhotoIndex = 2
            this.isShowUploadReceipt = true
        },
        closeUploadReceipt() {
            this.isShowUploadReceipt = false
        },
        openSubmitReceiptConfirm() {
            if (!this.hasUploadedFile(this.receiptPhotos)) {
                this.$helper.toastErr(this, "Bukti diterima wajib diupload.")
                return
            }

            if (this.hasIncompleteUpload(this.receiptPhotos)) {
                this.$helper.toastErr(this, "Upload bukti diterima wajib dilengkapi atau dihapus.")
                return
            }

            if (!this.selectedReceiptItem || !this.selectedReceiptItem.id) {
                this.$helper.toastErr(this, "ID log kas kecil tidak ditemukan.")
                return
            }

            this.$bvModal.show("modal-confirm-upload-bukti-diterima-kas-kecil")
        },
        submitReceipt() {
            this.isSubmittingReceipt = true
            this.$axios.post(this.buildUploadReceiptUrl())
                .then(() => {
                    this.$bvModal.hide("modal-confirm-upload-bukti-diterima-kas-kecil")
                    this.closeUploadReceipt()
                    this.$helper.toastSucc(this, "Bukti diterima berhasil diupload.")
                    this.getExpenseLogs()
                }).catch((error) => {
                    this.$helper.parseError(this, error)
                }).finally(() => {
                    this.isSubmittingReceipt = false
                })
        },
        buildUploadReceiptUrl() {
            const params = {
                miniCashCabangLogId: this.selectedReceiptItem.id,
                photos: this.getUploadedUrls(this.receiptPhotos),
            }

            return `mini-cash/upload-bukti-pesanan-diterima?${this.buildQueryString(params)}`
        },
        createUploadFoto(index) {
            return {
                index,
                file: null,
                fileName: "",
                previewUrl: "",
                publicUrl: "",
                isUploading: false,
            }
        },
        addReceiptPhoto() {
            if (this.receiptPhotos.length >= MAX_UPLOAD_COUNT) {
                this.$helper.toastErr(this, `Maksimal upload ${MAX_UPLOAD_COUNT} file.`)
                return
            }

            this.receiptPhotos.push(this.createUploadFoto(this.nextReceiptPhotoIndex))
            this.nextReceiptPhotoIndex += 1
        },
        removeReceiptPhoto(index) {
            if (this.receiptPhotos.length <= 1) {
                this.revokeUploadPreview(this.receiptPhotos[0])
                this.receiptPhotos = [this.createUploadFoto(1)]
                return
            }
            this.receiptPhotos
                .filter((item) => item.index === index)
                .forEach((item) => this.revokeUploadPreview(item))
            this.receiptPhotos = this.receiptPhotos.filter((item) => item.index !== index)
        },
        hasUploadedFile(files) {
            return files.some((item) => !this.$helper.isNullOrEmpty(item.publicUrl))
        },
        hasIncompleteUpload(files) {
            return files.some((item) => this.$helper.isNullOrEmpty(item.publicUrl))
        },
        getUploadedUrls(files) {
            return files
                .map((item) => this.getUploadPath(item.publicUrl))
                .filter((url) => !this.$helper.isNullOrEmpty(url))
                .join(",")
        },
        getUploadPath(url) {
            if (this.$helper.isNullOrEmpty(url)) return ""

            return String(url).replace(/^https?:\/\/[^/]+\//, "")
        },
        buildQueryString(params) {
            return Object.keys(params)
                .map((key) => {
                    const value = params[key]
                    const normalizedValue = key === "photos" ? value : encodeURIComponent(value)
                    return `${encodeURIComponent(key)}=${normalizedValue}`
                })
                .join("&")
        },
        async onUploadFile(item, selectedFile = null) {
            const file = selectedFile || item.file
            if (!file) return

            if (!this.isValidUploadFile(file)) {
                this.resetUploadFile(item)
                this.$helper.toastErr(this, "File harus berupa foto atau PDF.")
                return
            }

            item.isUploading = true
            this.revokeUploadPreview(item)
            item.publicUrl = ""
            item.fileName = ""
            this.$store.dispatch("loading/show", { progress: 0 })

            let response
            try {
                response = await this.$helper.uploadPresigned({
                    vue: this,
                    path: "mini-cash/create-presigned-url-proof",
                    params: { fileExtension: this.getFileExtension(file) },
                    fileUpload: file,
                    contentType: this.getUploadContentType(file),
                    onUploadProgress: progress => this.setUploadProgress(progress),
                    useXhr: true,
                })
            } catch (error) {
                this.$helper.parseError(this, error)
            } finally {
                item.isUploading = false
                this.$store.dispatch("loading/hide")
            }

            if (!response || !response.success) {
                this.resetUploadFile(item)
                this.$helper.toastErr(this, "Gagal upload file. Silakan coba lagi.")
                return
            }

            item.previewUrl = URL.createObjectURL(file)
            item.publicUrl = response.publicUrl
            item.fileName = file.name
        },
        setUploadProgress(progress) {
            this.$store.dispatch("loading/setProgress", Math.min(progress, 100))
        },
        resetUploadFile(item) {
            this.revokeUploadPreview(item)
            item.file = null
            item.fileName = ""
            item.publicUrl = ""
        },
        revokeUploadPreview(item) {
            if (!item.previewUrl) return

            URL.revokeObjectURL(item.previewUrl)
            item.previewUrl = ""
        },
        openFilePreview(item, label) {
            if (!item.previewUrl) return

            this.activeFilePreview = {
                title: `Preview ${label}`,
                type: this.getPreviewType(item.file),
                url: item.previewUrl,
            }
            this.$bvModal.show("modal-preview-file-kas-kecil-receipt")
        },
        getPreviewType(file) {
            if (!file) return "image"
            const type = file.type || ""
            const name = (file.name || "").toLowerCase()

            return type === "application/pdf" || name.endsWith(".pdf") ? "pdf" : "image"
        },
        getUploadContentType(file) {
            return (file && file.type) || "image"
        },
        getFileExtension(file) {
            const name = (file && file.name) || ""
            const extension = name.split(".").pop()
            if (extension && extension !== name) return extension.toLowerCase()

            const type = (file && file.type) || ""
            return type.split("/").pop() || ""
        },
        isValidUploadFile(file) {
            const type = (file && file.type) || ""
            const name = ((file && file.name) || "").toLowerCase()

            return type.startsWith("image/") || type === "application/pdf" || name.endsWith(".pdf")
        },
        formatCurrency(value) {
            return `Rp. ${this.$helper.addThousandSeparator(Number(value || 0))}`
        },
        formatCurrencyInput(value) {
            if (this.$helper.isNullOrEmpty(value)) return ""
            const cleanValue = this.$helper.changeToValue(String(value))
            return cleanValue.replace(/(?!^-)[^0-9]/g, "")
                .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
        },
        validateConfirmCurrentCash() {
            if (this.$helper.isNullOrEmpty(this.confirmCurrentCash)) {
                this.$helper.toastErr(this, "Kas Kecil Saat Ini wajib diisi.")
                return false
            }

            if (this.confirmCurrentCashValue !== this.currentCash) {
                this.$helper.toastErr(this, "Kas Kecil Saat Ini tidak sesuai.")
                return false
            }

            return true
        },
        formatDateTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value
            return `${this.$helper.changeDateFormat(value)} ${this.$helper.changeDateFormatGetHour(value)}`
        },
        getApprovalName(name, date) {
            if (this.isEmptyHistoryValue(name) && !this.$helper.isNullOrEmpty(date)) return "Auto Approve"
            return this.formatHistoryBy(name)
        },
        hasHistoryInfo(name, date) {
            return !this.isEmptyHistoryValue(name) || !this.$helper.isNullOrEmpty(date)
        },
        isEmptyHistoryValue(value) {
            return this.$helper.isNullOrEmpty(value) || String(value).toLowerCase().includes("null")
        },
        formatHistoryBy(value) {
            if (this.isEmptyHistoryValue(value)) return "-"
            return value
        },
        formatDate(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value
            return this.$helper.changeDateFormat(value)
        },
        formatTime(value) {
            if (this.$helper.isNullOrEmpty(value)) return "-"
            if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return ""
            return `${this.$helper.changeDateFormatGetHour(value)}`
        },
        getCashStatusLabel(status) {
            if (status === STATUS_KAS_KECIL.PENDING) return "Menunggu Approval CA"
            if (status === STATUS_KAS_KECIL.BERJALAN) return "Berjalan"
            if (status === STATUS_KAS_KECIL.REQUEST_CLOSED) return "Menunggu Approval EC"
            if (status === STATUS_KAS_KECIL.APPROVED_KC) return "Disetujui KC"
            if (status === STATUS_KAS_KECIL.PENDING_PENGEMBALIAN) return "Menunggu Pengembalian"
            if (status === STATUS_KAS_KECIL.CLOSED) return "Ditutup"
            return "-"
        },
        getCashStatusClass(status) {
            if (status === STATUS_KAS_KECIL.BERJALAN) return "status-box-md status-box-success"
            if (status === STATUS_KAS_KECIL.APPROVED_KC) return "status-box-md status-box-primary"
            if (status === STATUS_KAS_KECIL.PENDING_PENGEMBALIAN) return "status-box-md status-box-warning"
            if (status === STATUS_KAS_KECIL.CLOSED) return "status-box-md status-box-secondary"
            return "status-box-md status-box-warning"
        },
        getLogStatusLabel(status) {
            if (status === STATUS_LOG.OK) return "Barang/Jasa Diterima"
            if (status === STATUS_LOG.VOID) return "Void"
            if (status === STATUS_LOG.SEDANG_PEMESANAN) return "Sedang Pemesanan"
            if (status === STATUS_LOG.REFUND) return "Refund"
            return "-"
        },
        getLogStatusClass(status) {
            if (status === STATUS_LOG.OK) return "status-box-md status-box-success"
            if (status === STATUS_LOG.VOID) return "status-box-md status-box-danger"
            if (status === STATUS_LOG.REFUND) return "status-box-md status-box-secondary"
            return "status-box-md status-box-warning"
        },
        isLogChecked(item) {
            return Number(item.kcStatus || 0) === 1
        },
    },
    watch: {
        confirmCurrentCash(newValue) {
            const result = this.formatCurrencyInput(newValue)
            if (newValue === result) return
            this.$nextTick(() => this.confirmCurrentCash = result)
        },
    },
    created() {
        this.init()
    },
    beforeDestroy() {
        this.receiptPhotos.forEach((item) => this.revokeUploadPreview(item))
    },
}
</script>

<style scoped>
.kas-kecil-action-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 280px;
}

.kas-kecil-action-btn {
    min-width: 86px;
    white-space: normal;
    line-height: 1.2;
}

.kas-kecil-history-line {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 3px;
}

.kas-kecil-history-line > span:first-child {
    color: #8a97a5;
}

.kas-kecil-history-line span {
    min-width: 58px;
}

.kas-kecil-request-ec-btn {
    background-color: #EAB308;
    border-color: #EAB308;
    color: #fff;
}

.kas-kecil-request-ec-btn:hover,
.kas-kecil-request-ec-btn:focus {
    background-color: #CA8A04;
    border-color: #CA8A04;
    color: #fff;
}

.kas-kecil-approve-ec-btn {
    background-color: #0D9488;
    border-color: #0D9488;
    color: #fff;
}

.kas-kecil-approve-ec-btn:hover,
.kas-kecil-approve-ec-btn:focus {
    background-color: #0F766E;
    border-color: #0F766E;
    color: #fff;
}

.kas-kecil-log-toolbar {
    align-items: flex-end;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: 16px;
}

.kas-kecil-search-wrapper {
    margin-right: 16px;
    min-width: 300px;
}

.kas-kecil-search-row,
.kas-kecil-action-row {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
}

.kas-kecil-search-row .btn,
.kas-kecil-action-row .btn {
    margin-left: 8px;
}

.kas-kecil-search-row .btn:first-child,
.kas-kecil-action-row .btn:first-child {
    margin-left: 0;
}

.kas-kecil-action-row {
    justify-content: flex-end;
    margin-left: auto;
}

.kas-kecil-search-input {
    max-width: 180px;
    width: 180px;
}

.kas-kecil-status-badge {
    display: block;
    width: 100%;
    text-align: center;
}

.kas-kecil-notes-text {
    max-width: 150px;
    white-space: normal;
    word-break: break-word;
}

::v-deep .table td {
    padding-bottom: 10px;
    padding-top: 10px;
    vertical-align: top;
}

::v-deep .kas-kecil-date-col {
    width: 9%;
}

::v-deep .kas-kecil-invoice-col {
    width: 13%;
}

::v-deep .kas-kecil-item-col {
    width: 14%;
}

::v-deep .kas-kecil-total-col {
    width: 9%;
}

::v-deep .kas-kecil-notes-col {
    min-width: 150px;
    width: 15%;
}

::v-deep .kas-kecil-status-col {
    width: 16%;
}

::v-deep .kas-kecil-action-col {
    width: 24%;
}

@media (max-width: 575.98px) {
    .kas-kecil-log-toolbar {
        align-items: stretch;
        display: block;
    }

    .kas-kecil-search-wrapper {
        margin-right: 0;
        min-width: 0;
        width: 100%;
    }

    .kas-kecil-search-row {
        flex-wrap: nowrap;
        width: 100%;
    }

    .kas-kecil-search-input {
        flex: 1 1 auto;
        max-width: none;
        min-width: 0;
        width: auto;
    }

    .kas-kecil-search-row .btn {
        flex: 0 0 auto;
        margin-left: 8px;
    }

    .kas-kecil-action-row {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-gap: 8px;
        margin-left: 0;
        margin-top: 8px;
        width: 100%;
    }

    .kas-kecil-action-row .btn {
        line-height: 1.2;
        margin-left: 0;
        min-height: 34px;
        min-width: 0;
        padding-left: 8px;
        padding-right: 8px;
        white-space: normal;
        width: 100%;
    }

    .kas-kecil-action-wrapper {
        flex-direction: column;
        align-items: stretch;
    }

    .kas-kecil-action-btn {
        width: 86px;
        min-height: 32px;
    }
}
</style>
